import { useState, useMemo, useEffect, useCallback } from 'react';
import '@/styles/permissionsStyles.scss';
import '@/styles/PropertyStyles.scss';
import '@/styles/SettingsPage.scss';
import FinanceConfigPage from '../SettingsPages/FinanceConfigPage';
import UserPermissionsPage from '../PermissionPages/UserPermissionsPage';
import ViewUsers from '../UserPages/ViewUsers';
import CreateUser from '../UserPages/CreateUser';
import DepartmentManagement from '../SettingsPages/DepartmentManagement';
import FunctionManagement from '../SettingsPages/FunctionManagement';
import CsiProgrammesInputPage from '../MarketingPages/CsiProgrammesInputPage';
import OpsStatsForm from '../DataInputsTemp/Operations/OpsStatsForm';
import OpsIncidentForm from '../DataInputsTemp/Operations/OpsIncidentForm';
import CsiProgrammesDataPage from '../MarketingPages/CsiProgrammesDataPage';
import BloodhoundForm from '../DataInputsTemp/Operations/BloodhoundForm';
import OpsCertificates from '../DataInputsTemp/Operations/OpsCertificates';
import ViewProperties from '../PropertyPages/ViewProperties';
import CreateProperty from '../PropertyPages/CreateProperty';
import PropertyGroupsPage from '../PropertyPages/PropertyGroupsPage';
import PropertyAccessTab from '../PermissionPages/PropertyAccessTab';
import SiteVisitsInputPage from '../OperationsPages/SiteVisitsInputPage';
import PaidToSellerInputPage from '../DataInputsTemp/Finance/PaidToSellerInputPage';
import TempTenancyStats from '../DataInputsTemp/Leasing/TempTenancyStats';
import { useAuth } from '../../context/AuthContext';
import EnquiryOnlyWrapper from '../../components/EnquiryOnlyWrapper';
import SecretsPage from '../SettingsPages/SecretsPage';
import SystemSettingsPage from '../SettingsPages/SystemSettingsPage';
import TempSustainabilityStats from '../DataInputsTemp/Operations/TempSustainabilityStats';
import FicaInput from '../DataInputsTemp/Leasing/FicaInput';
import UnsignedLeasesInput from '../DataInputsTemp/Leasing/UnsignedLeasesInput';
import WasteTypeInput from '../DataInputsTemp/Operations/WasteTypeInput';
import WasteManagementInput from '../DataInputsTemp/Operations/WasteManagementInput';
import SustainabilityPortfolioStats from '../DataInputsTemp/Operations/SustainabilityPortfolioStats';
import SolarProjectInput from '../DataInputsTemp/Operations/SolarProjectInput';
import TemplateManagementPage from './TemplateManagementPage';
import BoardTemplateManagementPage from './BoardTemplateManagementPage';
import FileStorageConfigPage from './FileStorageConfigPage';
import ModuleTemplateConfigPage from './ModuleTemplateConfigPage';
import ScheduledTasksPage from './ScheduledTasksPage';
import EmailTemplatesPage from './EmailTemplatesPage';
import EmailMailboxesPage from './EmailMailboxesPage';
import ChecklistReportingPage from './ChecklistReportingPage';
import SipaReportingPage from '../Sipa/SipaReportingPage';
import SipaSettingsPage from './SipaSettingsPage';
import PropertyExternalMappingsPage from '../PropertyPages/PropertyExternalMappingsPage';
import RouterConfigurationPage from './RouterConfigurationPage';
import RouterMonitoringSettingsPage from '../IT/RouterMonitoringSettingsPage';
import RouterProvisioningSettingsPage from '../IT/RouterProvisioningSettingsPage';
import RouterFirewallTemplatesPage from '../IT/RouterFirewallTemplatesPage';
import RouterUnifiedAddressListsPage from '../IT/RouterUnifiedAddressListsPage';
import ServiceProvidersPage from './ServiceProvidersPage';
import ServiceContractsPage from './ServiceContractsPage';
import ITServiceDeskPage from '../IT/ITServiceDeskPage';
import EndpointHealthConfigPage from '../SettingsPages/EndpointHealthConfigPage';
import AssetConfigPage from './AssetConfigPage';
import AssetServiceTypesConfigPage from './AssetServiceTypesConfigPage';
import AssetWorkflowRulesPage from './AssetWorkflowRulesPage';
import FootfallQueryToolPage from '../../pages/ReportingPages/FootfallQueryToolPage';
import NotificationChannelsPage from './NotificationChannelsPage';
import BoardCreationRulesPage from './BoardCreationRulesPage';
import OtherLegalMatters from '../LegalPages/OtherLegalMatters';
import ShortPostingInputPage from '../OperationsPages/ShortPostingInputPage';
import { IoSearch, IoArrowBack, IoClose, IoStarOutline, IoStar, IoTimeOutline } from 'react-icons/io5';
import {
    IoSettingsSharp, IoHardwareChipOutline, IoHeadsetOutline, IoGlobeOutline,
    IoBusinessOutline, IoTicketOutline, IoMailOutline, IoClipboardOutline,
    IoLeafOutline, IoSearchCircleOutline, IoHomeOutline, IoDocumentTextOutline,
    IoCashOutline, IoMegaphoneOutline, IoStatsChartOutline
} from 'react-icons/io5';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Modal component for displaying content in an overlay.
 */
const Modal = ({ isOpen, children }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {children}
            </div>
        </div>
    );
};

/** localStorage key for pinned settings — per-user favorites for quick access. */
const PINNED_STORAGE_KEY = 'captrac.settings.pinned';
/** localStorage key for recently-opened settings. */
const RECENTS_STORAGE_KEY = 'captrac.settings.recents';
/** Maximum number of recents to remember. */
const RECENTS_MAX = 5;
/** Domains group the 15 categories into three navigable sections. */
const DOMAINS = [
    { id: 'it-admin', name: 'IT & Admin', categoryIds: ['administration', 'system', 'servicedesk', 'network', 'vendors', 'actionitems', 'communications'] },
    { id: 'operations', name: 'Operations & Data', categoryIds: ['operations', 'sustainability', 'sipainspections', 'properties', 'leasing', 'assetmanagement'] },
    { id: 'business', name: 'Business & Reports', categoryIds: ['finance', 'marketing', 'reporting'] }
];

/**
 * Category icon registry — mapped by category id so we render a consistent,
 * cross-platform icon set instead of emoji (which varies by OS/font).
 */
const CATEGORY_ICONS = {
    administration: IoSettingsSharp,
    system: IoHardwareChipOutline,
    servicedesk: IoHeadsetOutline,
    network: IoGlobeOutline,
    vendors: IoBusinessOutline,
    actionitems: IoTicketOutline,
    communications: IoMailOutline,
    operations: IoClipboardOutline,
    sustainability: IoLeafOutline,
    sipainspections: IoSearchCircleOutline,
    properties: IoHomeOutline,
    leasing: IoDocumentTextOutline,
    finance: IoCashOutline,
    marketing: IoMegaphoneOutline,
    reporting: IoStatsChartOutline
};

/**
 * SettingsPage Component
 *
 * Provides a categorized navigation system for all application settings.
 * UX model:
 * - Three top-level domains segment the 15 categories for fast scanning.
 * - A live search filters category, setting, and description text with a result count.
 * - Pinned (favorite) settings appear in a Quick Access row.
 * - Esc key returns from a sub-tab to the overview.
 * - Sub-tabs render from a single component registry to keep rendering declarative.
 */
const SettingsPage = () => {
    const { hasPermission } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => location.state?.tab || 'overview');
    const [showModal, setShowModal] = useState(false);
    const [refreshProperties, setRefreshProperties] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeDomain, setActiveDomain] = useState('all');
    const [pinnedIds, setPinnedIds] = useState(() => {
        try {
            const raw = localStorage.getItem(PINNED_STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [recentIds, setRecentIds] = useState(() => {
        try {
            const raw = localStorage.getItem(RECENTS_STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });

    // Persist pinned set whenever it changes.
    useEffect(() => {
        try {
            localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinnedIds));
        } catch {
            /* ignore quota errors — pinning is a convenience, not a requirement */
        }
    }, [pinnedIds]);

    // Persist recents whenever they change.
    useEffect(() => {
        try {
            localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recentIds));
        } catch {
            /* ignore */
        }
    }, [recentIds]);

    // Esc returns to the overview when inside a sub-tab so keyboard users don't
    // have to reach for the Back button.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && activeTab !== 'overview' && !showModal) {
                setActiveTab('overview');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [activeTab, showModal]);

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => {
        setShowModal(false);
        setRefreshProperties(prev => prev + 1);
    };

    const handleBackToOverview = () => setActiveTab('overview');

    const togglePin = useCallback((id) => {
        setPinnedIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
    }, []);

    // ─────────────────────────────────────────────────────────
    // Permission checks
    // ─────────────────────────────────────────────────────────
    const canViewFinance = hasPermission('/settings/finance-config');
    const canViewPermissions = hasPermission('/admin/users/permissions');
    const canViewUsers = hasPermission('/admin/users/view');
    const canViewDepartments = hasPermission('/admin/departments');
    const canViewFunctions = hasPermission('/admin/functions');
    const canViewCsi = hasPermission('/marketing/csi-program-input');
    const canViewOpsForm = hasPermission('/settings/operations-compliance');
    const canViewOpsIncident = hasPermission('/settings/operations-incident');
    const canViewCsiProjects = hasPermission('/settings/csi-project-data');
    const canViewBloodhound = hasPermission('/settings/bloodhound');
    const canViewCertificates = hasPermission('/settings/certificates');
    const canViewProperties = hasPermission('/admin/view-properties');
    const canViewPropertyGroups = hasPermission('/admin/property-groups');
    const canViewPropertyAccess = hasPermission('/admin/property-access');
    const canViewPropertyExternalMappings = hasPermission('/admin/property-external-mappings');
    const canViewPaidToSellerInput = hasPermission('/settings/paid-to-seller');
    const canViewTempTenancyStats = hasPermission('/settings/tenancy-statistics');
    const canViewSecrets = hasPermission('/admin/secrets');
    const canViewSustainStats = hasPermission('/settings/sustainability-statistics');
    const canViewFicaInput = hasPermission('/settings/fica-input');
    const canViewUnsignedLeasesInput = hasPermission('/settings/unsigned-leases-input');
    const canViewWasteTypeInput = hasPermission('/settings/waste-type-input');
    const canViewWasteManagementInput = hasPermission('/settings/waste-management-input');
    const canViewSustainabilityPortfolioStats = hasPermission('/settings/sustain-port-stats');
    const canViewSolarProjectInput = hasPermission('/settings/solar-project-input');
    const canViewTemplateManagement = hasPermission('/admin/template-management');
    const canViewBoardTemplateManagement = hasPermission('/admin/board-template-management');
    const canViewFileStorageConfig = hasPermission('/admin/file-storage-config');
    const canViewModuleTemplateConfig = hasPermission('/admin/module-template-config');
    const canViewScheduledTasks = hasPermission('/settings/scheduled-tasks');
    const canViewEmailTemplates = hasPermission('/settings/email-templates');
    const canViewEmailMailboxes = hasPermission('/settings/email-mailboxes');
    const canViewChecklistReporting = hasPermission('/reporting/checklist-submissions');
    const canViewSipaReporting = hasPermission('/reporting/sipa-reporting');
    const canViewChecklistExceptionReport = hasPermission('/reporting/checklist-exception-report');
    const canViewSipaConfig = hasPermission('/sipa/designer');
    const canViewSipaDesigner = hasPermission('/sipa/designer');
    const canViewSipaWorkflowConfig = hasPermission('/sipa/workflow-config');
    const canViewSystemConfig = hasPermission('/admin/system-config');
    const canViewRouterConfig = hasPermission('/settings/router-config');
    const canViewRouterMonitoringSettings = hasPermission('/it/config-management/router-monitoring-settings');
    const canViewRouterProvisioningSettings = hasPermission('/it/config-management/router-provisioning-settings');
    const canViewRouterFirewallTemplates = hasPermission('/it/config-management/router-firewall-templates');
    const canViewRouterAddressLists = hasPermission('/it/config-management/router-address-lists');
    const canViewServiceProviders = hasPermission('/it/config-management/service-providers');
    const canViewItServiceDesk = hasPermission('/it/service-desk');
    const canViewAssetConfig = hasPermission('/operations/asset-config');
    const canViewAssetManagement = hasPermission('/operations/asset-management');
    const canViewFootfallQueryTool = hasPermission('/reporting/footfall-query-tool');
    const canViewIncidentReporting = hasPermission('/operations/incidents');
    const canViewNotificationChannels = hasPermission('/admin/notification-channels');
    const canViewBoardCreationRules = hasPermission('/admin/board-creation-rules');
    const canViewOtherLegalMatters = hasPermission('/legal/otherlegalmatters');
    const canViewAddSiteVisit = hasPermission('/operations/add-site-visit');
    const canViewShortPostings = hasPermission('/operations/add-short-postings');

    // ─────────────────────────────────────────────────────────
    // Settings catalog — each entry owns its own render function so we can
    // collapse the giant if-ladder into a single registry lookup.
    // ─────────────────────────────────────────────────────────
    const settingsCategories = useMemo(() => [
        {
            id: 'administration', name: 'Administration', icon: '⚙️',
            description: 'Manage users, permissions, departments, and functional areas that form the foundation of your CapTrac setup.',
            settings: [
                { id: 'users', name: 'User Management', canView: canViewUsers, route: '/admin/users/view', render: () => (
                    <>
                        <div className="users-header-row">
                            <h1>Users</h1>
                            <button onClick={handleOpenModal}>Add User</button>
                        </div>
                        <ViewUsers refreshTrigger={refreshProperties} />
                        <Modal isOpen={showModal} onClose={handleCloseModal}>
                            <CreateUser onClose={handleCloseModal} />
                        </Modal>
                    </>
                )},
                { id: 'permissions', name: 'User Permissions', canView: canViewPermissions, route: '/admin/users/permissions', render: () => <UserPermissionsPage /> },
                { id: 'departments', name: 'Departments', canView: canViewDepartments, route: '/admin/departments', render: () => <DepartmentManagement /> },
                { id: 'functions', name: 'Functions', canView: canViewFunctions, route: '/admin/functions', render: () => <FunctionManagement /> }
            ]
        },
        {
            id: 'system', name: 'System', icon: '🖥️',
            description: 'API keys, integration credentials, file storage, and automated task scheduling.',
            settings: [
                { id: 'secrets', name: 'App Secrets & Integrations', canView: canViewSecrets, route: '/admin/secrets', render: () => <SecretsPage onNavigateToTab={setActiveTab} /> },
                { id: 'fileStorageConfig', name: 'File Storage', canView: canViewFileStorageConfig, route: '/admin/file-storage-config', render: () => <FileStorageConfigPage /> },
                { id: 'systemConfig', name: 'System Configuration', canView: canViewSystemConfig, route: '/admin/system-config', render: () => <SystemSettingsPage /> },
                { id: 'scheduledTasks', name: 'Scheduled Tasks', canView: canViewScheduledTasks, route: '/admin/scheduled-tasks', render: () => <ScheduledTasksPage /> }
            ]
        },
        {
            id: 'servicedesk', name: 'Service Desk', icon: '🎧',
            description: 'IT Service Desk ticket types, SLA policies, and ticket routing configuration.',
            settings: [
                { id: 'itServiceDesk', name: 'IT Service Desk', canView: canViewItServiceDesk, route: '/it/service-desk', render: () => <ITServiceDeskPage /> },
                { id: 'endpointHealthConfig', name: 'Endpoint Health Monitoring', canView: canViewItServiceDesk, route: '/it/config-management/endpoint-health', render: () => <EndpointHealthConfigPage /> }
            ],
            upcoming: []
        },
        {
            id: 'network', name: 'Network & Infrastructure', icon: '🌐',
            description: 'MikroTik router configuration, WireGuard VPN setup, monitoring thresholds, provisioning templates, and firewall rules.',
            settings: [
                { id: 'routerConfig', name: 'Router Configuration & Setup', canView: canViewRouterConfig, route: '/settings/router-config', render: () => <RouterConfigurationPage /> },
                { id: 'routerMonitoringSettings', name: 'Router Monitoring', canView: canViewRouterMonitoringSettings, route: '/it/config-management/router-monitoring-settings', render: () => <RouterMonitoringSettingsPage /> },
                { id: 'routerProvisioningSettings', name: 'Router Provisioning', canView: canViewRouterProvisioningSettings, route: '/it/config-management/router-provisioning-settings', render: () => <RouterProvisioningSettingsPage /> },
                { id: 'routerFirewallTemplates', name: 'Firewall Templates', canView: canViewRouterFirewallTemplates, route: '/it/config-management/router-firewall-templates', render: () => <RouterFirewallTemplatesPage /> },
                { id: 'routerAddressLists', name: 'Unified Address Lists', canView: canViewRouterAddressLists, route: '/it/config-management/router-address-lists', render: () => <RouterUnifiedAddressListsPage /> }
            ]
        },
        {
            id: 'vendors', name: 'Vendors & Contracts', icon: '🏢',
            description: 'Service provider registry and contract management for third-party vendors.',
            settings: [
                { id: 'serviceProviders', name: 'Service Provider Registry', canView: canViewServiceProviders, route: '/it/config-management/service-providers', render: () => <ServiceProvidersPage /> },
                { id: 'serviceProviderContracts', name: 'Service Contracts', canView: canViewServiceProviders, route: '/it/config-management/service-providers', render: () => <ServiceContractsPage /> }
            ]
        },
        {
            id: 'actionitems', name: 'Action Items', icon: '🎫',
            description: 'Configure the 3-step action item workflow: design item templates → assign them to modules → control who can create each type.',
            settings: [
                { id: 'templateManagement', name: 'Step 1: Item Templates', canView: canViewTemplateManagement, route: '/admin/template-management', render: () => <TemplateManagementPage /> },
                { id: 'moduleTemplateConfig', name: 'Step 2: Module Assignments', canView: canViewModuleTemplateConfig, route: '/admin/module-template-config', render: () => <ModuleTemplateConfigPage onNavigateToTab={setActiveTab} /> },
                { id: 'boardCreationRules', name: 'Step 3: Board Creation Rules', canView: canViewBoardCreationRules, route: '/admin/board-creation-rules', render: () => <BoardCreationRulesPage onNavigateToTab={setActiveTab} /> },
                { id: 'boardTemplateManagement', name: 'Board Templates', canView: canViewBoardTemplateManagement, route: '/admin/board-template-management', render: () => <BoardTemplateManagementPage /> }
            ]
        },
        {
            id: 'communications', name: 'Communications', icon: '📨',
            description: 'Email mailboxes, notification delivery channels (Email, Teams, WhatsApp), and email template design.',
            settings: [
                { id: 'notificationChannels', name: 'Notification Channels', canView: canViewNotificationChannels, route: '/admin/notification-channels', render: () => <NotificationChannelsPage /> },
                { id: 'emailTemplates', name: 'Email Templates', canView: canViewEmailTemplates, route: '/settings/email-templates', render: () => <EmailTemplatesPage /> },
                { id: 'emailMailboxes', name: 'Email Mailboxes', canView: canViewEmailMailboxes, route: '/settings/email-mailboxes', render: () => <EmailMailboxesPage /> }
            ]
        },
        {
            id: 'operations', name: 'Operations & Compliance', icon: '📋',
            description: 'Operational forms, certificates, incident management, and asset configuration.',
            settings: [
                { id: 'bloodhound', name: 'Bloodhound Form', canView: canViewBloodhound, route: '/settings/bloodhound', render: () => <BloodhoundForm /> },
                { id: 'certificates', name: 'Certificates', canView: canViewCertificates, route: '/settings/certificates', render: () => <OpsCertificates /> },
                { id: 'opsForm', name: 'Compliance Form', canView: canViewOpsForm, route: '/settings/operations-compliance', render: () => <OpsStatsForm /> },
                { id: 'opsIncidentForm', name: 'Incident Form', canView: canViewOpsIncident, route: '/settings/operations-incident', render: () => <OpsIncidentForm /> },
                { id: 'addSiteVisit', name: 'Add Site Visit', canView: canViewAddSiteVisit, route: '/operations/add-site-visit', render: () => <SiteVisitsInputPage /> },
                { id: 'shortPostingsInput', name: 'Short Postings', canView: canViewShortPostings, route: '/operations/add-short-postings', render: () => <ShortPostingInputPage /> }
            ]
        },
        {
            id: 'sustainability', name: 'Sustainability', icon: '🌱',
            description: 'Environmental metrics, solar projects, waste management tracking, and portfolio statistics.',
            settings: [
                { id: 'sustainabilityPortfolioStats', name: 'Portfolio Stats', canView: canViewSustainabilityPortfolioStats, route: '/settings/sustain-port-stats', render: () => <SustainabilityPortfolioStats /> },
                { id: 'solarProjectInput', name: 'Solar Project Input', canView: canViewSolarProjectInput, route: '/settings/solar-project-input', render: () => <SolarProjectInput /> },
                { id: 'tempSustainStats', name: 'Sustainability Statistics', canView: canViewSustainStats, route: '/settings/sustainability-statistics', render: () => <TempSustainabilityStats /> },
                { id: 'wasteManagementInput', name: 'Waste Management', canView: canViewWasteManagementInput, route: '/settings/waste-management-input', render: () => <WasteManagementInput /> },
                { id: 'wasteTypeInput', name: 'Waste Types', canView: canViewWasteTypeInput, route: '/settings/waste-type-input', render: () => <WasteTypeInput /> } 
            ]
        },
        {
            id: 'sipainspections', name: 'SIPA Inspections', icon: '🔍',
            description: 'Design inspection templates, configure scoring workflows, and set sign-off rules.',
            settings: [
                { id: 'sipaSettings', name: 'SIPA Configuration', canView: canViewSipaDesigner || canViewSipaConfig || canViewSipaWorkflowConfig, route: '/sipa/designer', render: () => <SipaSettingsPage /> }
            ],
            upcoming: [
                { name: 'Inspection Scheduling' },
                { name: 'Findings Library' }
            ]
        },
        {
            id: 'properties', name: 'Properties', icon: '🏘️',
            description: 'Property details, groupings, user access permissions, and external system mappings.',
            settings: [
                { id: 'properties', name: 'Property Management', canView: canViewProperties, route: '/admin/view-properties', render: () => (
                    <>
                        <div className="properties-header-row">
                            <h1>Properties</h1>
                            <button onClick={handleOpenModal}>Add Property</button>
                        </div>
                        <ViewProperties refreshTrigger={refreshProperties} />
                        <Modal isOpen={showModal} onClose={handleCloseModal}>
                            <CreateProperty onClose={handleCloseModal} />
                        </Modal>
                    </>
                )},
                { id: 'propertyGroups', name: 'Property Groups', canView: canViewPropertyGroups, route: '/admin/property-groups', render: () => <PropertyGroupsPage /> },
                { id: 'propertyAccess', name: 'Access Permissions', canView: canViewPropertyAccess, route: '/admin/property-access', render: () => <PropertyAccessTab /> },
                { id: 'propertyExternalMappings', name: 'External Mappings', canView: canViewPropertyExternalMappings, route: '/admin/property-external-mappings', render: () => <PropertyExternalMappingsPage /> }
            ]
        },
        {
            id: 'leasing', name: 'Leasing', icon: '📝',
            description: 'FICA documentation, tenancy statistics, and unsigned lease tracking.',
            settings: [
                { id: 'ficaInput', name: 'FICA Input', canView: canViewFicaInput, route: '/settings/fica-input', render: () => <FicaInput /> },
                { id: 'tempTenancyStats', name: 'Tenancy Statistics', canView: canViewTempTenancyStats, route: '/settings/tenancy-statistics', render: () => <TempTenancyStats /> },
                { id: 'unsignedLeasesInput', name: 'Unsigned Leases', canView: canViewUnsignedLeasesInput, route: '/settings/unsigned-leases-input', render: () => <UnsignedLeasesInput /> },
                { id: 'otherLegalMatters', name: 'Other Legal Matters', canView: canViewOtherLegalMatters, route: '/legal/otherlegalmatters', render: () => <OtherLegalMatters /> }
            ]
        },
        {
            id: 'assetmanagement', name: 'Asset Management', icon: '🔧',
            description: 'Asset types, categories, workflow rules, scheduled maintenance tasks, and reporting.',
            settings: [
                { id: 'assetConfig', name: 'Asset Categories', canView: canViewAssetConfig, route: '/operations/asset-config', render: () => <AssetConfigPage /> },
                { id: 'assetServiceTypes', name: 'Service Types', canView: canViewAssetManagement, route: '/settings/asset-service-types', render: () => <AssetServiceTypesConfigPage /> },
                { id: 'assetWorkflowRules', name: 'Workflow Rules', canView: canViewAssetManagement, route: '/settings/asset-workflow-rules', render: () => <AssetWorkflowRulesPage /> }
            ]
        },
        {
            id: 'finance', name: 'Finance', icon: '💰',
            description: 'Financial configuration and paid-to-seller record management.',
            settings: [
                { id: 'finance', name: 'Finance Configuration', canView: canViewFinance, route: '/settings/finance-config', render: () => <FinanceConfigPage /> },
                { id: 'paidToSeller', name: 'Paid To Seller Input', canView: canViewPaidToSellerInput, route: '/settings/paid-to-seller', render: () => <PaidToSellerInputPage /> }
            ]
        },
        {
            id: 'marketing', name: 'Marketing & CSI', icon: '📣',
            description: 'Corporate Social Investment programmes and project data management.',
            settings: [
                { id: 'csiInput', name: 'CSI Programmes', canView: canViewCsi, route: '/marketing/csi-program-input', render: () => <CsiProgrammesInputPage /> },
                { id: 'csiProjects', name: 'CSI Projects Data', canView: canViewCsiProjects, route: '/settings/csi-project-data', render: () => <CsiProgrammesDataPage /> }
            ]
        },
        {
            id: 'reporting', name: 'Reports & Analytics', icon: '📊',
            description: 'Checklist reports, SIPA analytics, incident summaries, and footfall analysis tools.',
            settings: [
                { id: 'checklistReporting', name: 'Checklist Reporting', canView: canViewChecklistReporting || canViewChecklistExceptionReport, route: '/reporting/checklist-reporting', render: () => <ChecklistReportingPage initialTab="checklists" showExceptionTab={canViewChecklistExceptionReport} showChecklistTab={canViewChecklistReporting} /> },
                { id: 'sipaReporting', name: 'SIPA Reporting', canView: canViewSipaReporting, route: '/reporting/sipa-reporting', render: () => <SipaReportingPage /> },
                { id: 'incidentReporting', name: 'Incident Reporting', canView: canViewIncidentReporting, route: '/operations/incidents', isExternalRoute: true },
                { id: 'footfallQueryTool', name: 'Footfall Query Tool', canView: canViewFootfallQueryTool, route: '/reporting/footfall-query-tool', render: () => <FootfallQueryToolPage /> }
            ]
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [
        canViewFinance, canViewPermissions, canViewUsers, canViewDepartments, canViewFunctions,
        canViewCsi, canViewOpsForm, canViewOpsIncident, canViewCsiProjects, canViewBloodhound,
        canViewCertificates, canViewProperties, canViewPropertyGroups, canViewPropertyAccess,
        canViewPropertyExternalMappings, canViewPaidToSellerInput, canViewTempTenancyStats,
        canViewSecrets, canViewSustainStats, canViewFicaInput, canViewUnsignedLeasesInput,
        canViewWasteTypeInput, canViewWasteManagementInput, canViewSustainabilityPortfolioStats,
        canViewSolarProjectInput, canViewTemplateManagement, canViewBoardTemplateManagement,
        canViewFileStorageConfig, canViewModuleTemplateConfig, canViewScheduledTasks,
        canViewEmailTemplates, canViewEmailMailboxes, canViewChecklistReporting, canViewSipaReporting,
        canViewChecklistExceptionReport, canViewSipaConfig, canViewSipaDesigner, canViewSipaWorkflowConfig,
        canViewSystemConfig, canViewRouterConfig, canViewRouterMonitoringSettings,
        canViewRouterProvisioningSettings, canViewRouterFirewallTemplates, canViewRouterAddressLists,
        canViewServiceProviders, canViewItServiceDesk, canViewAssetConfig, canViewAssetManagement, canViewFootfallQueryTool,
        canViewIncidentReporting, canViewNotificationChannels, canViewBoardCreationRules,
        refreshProperties, showModal, canViewAddSiteVisit
    ]);

    // Flat setting lookup for O(1) access by id.
    const settingsById = useMemo(() => {
        const map = new Map();
        for (const c of settingsCategories) {
            for (const s of c.settings) map.set(s.id, { ...s, categoryId: c.id, categoryName: c.name });
        }
        return map;
    }, [settingsCategories]);

    const activeSetting = activeTab !== 'overview' ? settingsById.get(activeTab) : null;
    const activeTabRoute = activeSetting?.route || null;

    // Domain-filtered categories (before search is applied).
    const categoriesInActiveDomain = useMemo(() => {
        if (activeDomain === 'all') return settingsCategories;
        const domain = DOMAINS.find(d => d.id === activeDomain);
        if (!domain) return settingsCategories;
        return settingsCategories.filter(c => domain.categoryIds.includes(c.id));
    }, [activeDomain, settingsCategories]);

    // Live search across category name, setting name, and description.
    // Categories and settings are sorted alphabetically so the overview is easy to scan.
    const filteredCategories = useMemo(() => {
        const sortSettings = (arr) => [...arr].sort((a, b) => a.name.localeCompare(b.name));
        const sortUpcoming = (arr) => [...(arr || [])].sort((a, b) => a.name.localeCompare(b.name));

        const base = categoriesInActiveDomain.map(c => ({
            ...c,
            settings: sortSettings(c.settings),
            upcoming: sortUpcoming(c.upcoming)
        }));

        if (!searchQuery.trim()) {
            return [...base].sort((a, b) => a.name.localeCompare(b.name));
        }
        const q = searchQuery.trim().toLowerCase();
        return base
            .map(category => ({
                ...category,
                settings: category.settings.filter(s =>
                    s.name.toLowerCase().includes(q) ||
                    category.name.toLowerCase().includes(q) ||
                    (category.description || '').toLowerCase().includes(q)
                )
            }))
            .filter(c => c.settings.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [categoriesInActiveDomain, searchQuery]);

    const visibleSettingCount = useMemo(
        () => filteredCategories.reduce((n, c) => n + c.settings.filter(s => s.canView).length, 0),
        [filteredCategories]
    );

    /**
     * Domains that have at least one visible (permission-checked, non-placeholder)
     * setting. Hiding empty domain tabs keeps the toolbar tidy for smaller orgs.
     */
    const visibleDomains = useMemo(() => {
        return DOMAINS.filter(d => {
            const categoryIds = new Set(d.categoryIds);
            return settingsCategories.some(c =>
                categoryIds.has(c.id) && c.settings.some(s => s.canView)
            );
        });
    }, [settingsCategories]);

    const pinnedSettings = useMemo(
        () => pinnedIds.map(id => settingsById.get(id)).filter(s => s && s.canView),
        [pinnedIds, settingsById]
    );

    const openSetting = (setting) => {
        if (!setting) return;
        // Track recents (most-recent first, dedupe, cap).
        setRecentIds(prev => {
            const next = [setting.id, ...prev.filter(id => id !== setting.id)].slice(0, RECENTS_MAX);
            return next;
        });
        if (setting.isExternalRoute) {
            navigate(setting.route);
            return;
        }
        setActiveTab(setting.id);
    };

    const recentSettings = useMemo(
        () => recentIds
            .map(id => settingsById.get(id))
            .filter(s => s && s.canView && !pinnedIds.includes(s.id)),
        [recentIds, settingsById, pinnedIds]
    );

    // ─────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────
    return (
        <div className="permissions-management settings-fullwidth">
            <h1 className="settings-header">Settings</h1>
            <div className="settings-layout settings-layout-no-sidebar">
                <div className="settings-content">
                    {activeTab !== 'overview' && activeSetting && (
                        <nav className="settings-breadcrumb" aria-label="Settings breadcrumb">
                            <button className="btn-create read-only-allowed" onClick={handleBackToOverview} title="Back to Settings (Esc)">
                                <IoArrowBack size={12} />
                                <span>Back to Settings</span>
                            </button>
                            <span className="settings-breadcrumb-sep">/</span>
                            <span className="settings-breadcrumb-category">{activeSetting.categoryName}</span>
                            <span className="settings-breadcrumb-sep">/</span>
                            <span className="settings-breadcrumb-current">{activeSetting.name}</span>
                        </nav>
                    )}

                    {activeTab === 'overview' && (
                        <div className="settings-overview">
                            <div className="settings-overview-toolbar is-sticky">
                                <div className="settings-search settings-overview-search">
                                    <IoSearch size={18} className="search-icon" aria-hidden="true" />
                                    <input
                                        type="text"
                                        placeholder="Search settings..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="search-input read-only-allowed"
                                        aria-label="Search settings"
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            className="settings-search-clear read-only-allowed"
                                            onClick={() => setSearchQuery('')}
                                            aria-label="Clear search"
                                        >
                                            <IoClose size={16} />
                                        </button>
                                    )}
                                </div>

                                <div className="settings-domain-tabs" role="tablist" aria-label="Settings domain">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={activeDomain === 'all'}
                                        className={`settings-domain-tab read-only-allowed${activeDomain === 'all' ? ' is-active' : ''}`}
                                        onClick={() => setActiveDomain('all')}
                                    >
                                        All
                                    </button>
                                    {DOMAINS.map(d => {
                                        const isVisible = visibleDomains.some(v => v.id === d.id);
                                        if (!isVisible && activeDomain !== d.id) return null;
                                        return (
                                            <button
                                                key={d.id}
                                                type="button"
                                                role="tab"
                                                aria-selected={activeDomain === d.id}
                                                className={`settings-domain-tab read-only-allowed${activeDomain === d.id ? ' is-active' : ''}`}
                                                onClick={() => setActiveDomain(d.id)}
                                            >
                                                {d.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <h1>Application Settings</h1>
                            <p className="overview-intro">
                                Configure and manage various aspects of the CapTrac system. Pin the settings you use
                                most often for one-click access, or search across every domain.
                            </p>

                            {searchQuery && (
                                <p className="settings-result-count" aria-live="polite">
                                    {visibleSettingCount === 0
                                        ? <>No settings match <strong>&ldquo;{searchQuery}&rdquo;</strong>.</>
                                        : <>{visibleSettingCount} setting{visibleSettingCount === 1 ? '' : 's'} match <strong>&ldquo;{searchQuery}&rdquo;</strong>.</>}
                                </p>
                            )}

                            {pinnedSettings.length > 0 && !searchQuery && activeDomain === 'all' && (
                                <section className="settings-quick-access" aria-label="Pinned settings">
                                    <h3 className="settings-quick-access-title">
                                        <IoStar size={16} aria-hidden="true" /> Quick Access
                                    </h3>
                                    <div className="settings-quick-access-grid">
                                        {pinnedSettings.map(s => (
                                            <button
                                                key={`pin-${s.id}`}
                                                type="button"
                                                className="settings-quick-access-chip read-only-allowed"
                                                onClick={() => openSetting(s)}
                                                title={`${s.categoryName} · ${s.name}`}
                                            >
                                                <span className="settings-quick-access-chip-name">{s.name}</span>
                                                <span className="settings-quick-access-chip-category">{s.categoryName}</span>
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {recentSettings.length > 0 && !searchQuery && activeDomain === 'all' && (
                                <section className="settings-quick-access settings-recents" aria-label="Recently opened settings">
                                    <h3 className="settings-quick-access-title">
                                        <IoTimeOutline size={16} aria-hidden="true" /> Recent
                                    </h3>
                                    <div className="settings-quick-access-grid">
                                        {recentSettings.map(s => (
                                            <button
                                                key={`recent-${s.id}`}
                                                type="button"
                                                className="settings-quick-access-chip read-only-allowed"
                                                onClick={() => openSetting(s)}
                                                title={`${s.categoryName} · ${s.name}`}
                                            >
                                                <span className="settings-quick-access-chip-name">{s.name}</span>
                                                <span className="settings-quick-access-chip-category">{s.categoryName}</span>
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            )}

                            <div className="settings-grid">
                                {filteredCategories.map(category => {
                                    const visibleSettings = category.settings.filter(s => s.canView);
                                    const placeholderSettings = (category.upcoming || []).filter(() => !searchQuery);
                                    if (visibleSettings.length === 0 && placeholderSettings.length === 0) return null;

                                    const Icon = CATEGORY_ICONS[category.id] || IoSettingsSharp;

                                    return (
                                        <div
                                            key={category.id}
                                            className="category-overview-card"
                                        >
                                            <div className="category-card-header">
                                                <span className="category-card-icon" aria-hidden="true">
                                                    <Icon size={22} />
                                                </span>
                                                <h3>{category.name}</h3>
                                                {visibleSettings.length > 0 && (
                                                    <span className="category-card-count" aria-label={`${visibleSettings.length} settings`}>
                                                        {visibleSettings.length}
                                                    </span>
                                                )}
                                            </div>
                                            {category.description && (
                                                <p className="category-card-description">{category.description}</p>
                                            )}
                                            <ul className="category-settings-list">
                                                {visibleSettings.map(setting => {
                                                    const isPinned = pinnedIds.includes(setting.id);
                                                    return (
                                                        <li key={setting.id} className="category-settings-item">
                                                            <button
                                                                className="setting-link read-only-allowed"
                                                                onClick={() => openSetting(setting)}
                                                            >
                                                                {setting.name}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`setting-pin-btn read-only-allowed${isPinned ? ' is-pinned' : ''}`}
                                                                onClick={(e) => { e.stopPropagation(); togglePin(setting.id); }}
                                                                aria-label={isPinned ? `Unpin ${setting.name}` : `Pin ${setting.name} for quick access`}
                                                                aria-pressed={isPinned}
                                                                title={isPinned ? 'Remove from Quick Access' : 'Pin to Quick Access'}
                                                            >
                                                                {isPinned ? <IoStar size={14} /> : <IoStarOutline size={14} />}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                {placeholderSettings.map(u => (
                                                    <li key={`upcoming-${u.name}`} className="category-settings-item category-settings-item--upcoming">
                                                        <span className="setting-link is-upcoming" title="Coming soon">
                                                            {u.name}
                                                            <span className="setting-upcoming-badge">Soon</span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    );
                                })}
                            </div>

                            {filteredCategories.length === 0 && (
                                <div className="settings-empty-state">
                                    <p>No settings available in this view.</p>
                                    {(searchQuery || activeDomain !== 'all') && (
                                        <button
                                            type="button"
                                            className="btn-create read-only-allowed"
                                            onClick={() => { setSearchQuery(''); setActiveDomain('all'); }}
                                        >
                                            Reset filters
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab !== 'overview' && (
                        <EnquiryOnlyWrapper pageRoute={activeTabRoute}>
                            {activeSetting && activeSetting.canView && activeSetting.render
                                ? activeSetting.render()
                                : (
                                    <div className="settings-empty-state">
                                        <p>You don&apos;t have permission to view this setting, or the page is unavailable.</p>
                                        <button type="button" className="btn-create read-only-allowed" onClick={handleBackToOverview}>
                                            Back to Settings
                                        </button>
                                    </div>
                                )}
                        </EnquiryOnlyWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
